import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Employee } from "../../../../types";
import { Color } from "../../../../constants/color";
import Label from "../../../label";
import { TriangleAlert } from "lucide-react";
import { codiceFiscaleDaCorreggere } from "../../../../features/employee/rilievoCodiceFiscale";

interface Props {
  employee: Employee;
}

/*
 `$anomalia` segnala una riga su cui c'e' qualcosa da correggere: oggi il solo caso e' il codice
 fiscale che non torna. Il colore non basta da solo — chi non distingue il rosso vedrebbe una riga
 come le altre — quindi va sempre insieme al triangolo, che ha una forma anche senza colore, e al
 `title` che dice a parole di che si tratta.
*/
const SingleParticipantsWrapper = styled.td<{ $anomalia: boolean }>`
  display: flex;
  align-items: center;
  min-height: 2.6rem;
  border-bottom: 1px solid ${Color.LIGHT_BLUE};
  background-color: ${({ $anomalia }) =>
    $anomalia ? Color.LIGHT_RED : Color.NONE};
  &:last-child {
    border-bottom: 0;
  }
`;

/* L'altezza di una riga di testo della tabella: le celle scrivono a 12px. */
const ALTEZZA_RIGA_TESTO = "14px";

/*
 Il posto del triangolo, largo uguale su ogni riga anche quando il triangolo non c'e': se lo
 slot comparisse solo sulle righe segnalate, le colonne di quelle righe slitterebbero a destra
 rispetto a tutte le altre. Prende lo spazio che prima era il rientro di `LabelContainer`, cosi'
 le colonne restano dov'erano rispetto all'intestazione, che un rientro suo non ce l'ha.
*/
const SlotRilievo = styled.div`
  display: flex;
  align-items: center;
  /*
   Il triangolo sta a sinistra dello slot, non al centro: al centro finisce appiccicato allo
   stato, e le due cose si leggono come una sola.
  */
  justify-content: flex-start;
  flex: 0 0 1.25rem;

  /*
   Alto quanto una riga di testo e ancorato in cima, non centrato sulla riga intera: una
   mansione lunga manda il proprio testo a capo e fa crescere la riga, e un triangolo centrato
   su tutta l'altezza finirebbe sotto le parole che sta segnalando. Cosi' resta accanto alla
   prima riga, dove c'e' lo stato, comunque sia alta la riga.
  */
  align-self: flex-start;
  height: ${ALTEZZA_RIGA_TESTO};

  /*
   Un pelo piu' su del centro esatto. Un triangolo ha la base pesante e il vertice leggero:
   centrato per calcolo, l'occhio lo vede caduto in basso rispetto alle lettere accanto.
  */
  svg {
    transform: translateY(-1px);
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-grow: 400;
  justify-content: start;
`;

const TruncatedDiv = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Cell = styled.td`
  padding: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SingleEmployee = ({ employee }: Props) => {
  const { t } = useTranslation();
  // Qui il codice fiscale si legge e basta, quindi niente codice a video: la regola, e il perche'
  // un campo assente non sia un'anomalia, stanno in `codiceFiscaleDaCorreggere`. Lo stesso
  // rilievo si rilegge per esteso nella scheda del dipendente, che e' dove si corregge.
  const daCorreggere = codiceFiscaleDaCorreggere(employee);

  return (
    <SingleParticipantsWrapper
      $anomalia={daCorreggere}
      title={
        daCorreggere ? t("employee-registry-invalid-fiscal-code") : undefined
      }
    >
      <LabelContainer>
        <SlotRilievo>
          {daCorreggere && (
            /*
           14px, non la misura di icona che si userebbe altrove: il triangolo disegnato occupa
           tre quarti del riquadro, e a questa misura resta nella fascia delle maiuscole del
           testo di riga, alte 9px. Piu' grande resta centrato lo stesso, ma sporge sopra e
           sotto le lettere e si legge come storto.
          */
            <TriangleAlert
              color={Color.RED}
              height={14}
              width={14}
              role="img"
              aria-label={t("employee-registry-invalid-fiscal-code")}
            />
          )}
        </SlotRilievo>
        <Label
          text={employee.state === 1 ? "Assunto" : "Cessato"}
          color={Color.LIGHT_BLUE}
          textAlign={"left"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"9rem"}
        />
        <Label
          text={employee.lastName}
          color={Color.LIGHT_BLUE}
          textAlign={"left"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"11rem"}
        />
        <Label
          text={employee.firstName}
          color={Color.LIGHT_BLUE}
          textAlign={"left"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"10rem"}
        />
        <Label
          text={employee.jobDescription}
          color={Color.LIGHT_BLUE}
          textAlign={"center"}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          width={"8rem"}
        />
        <Label
          text={employee.workplace}
          color={Color.LIGHT_BLUE}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          textAlign={"center"}
          width={"17rem"}
        />

        <TruncatedDiv>
          <Label
            text={employee.email}
            color={Color.LIGHT_BLUE}
            textTransform={"uppercase"}
            fontWeight={"normal"}
            fontSize={"10px"}
            textAlign={"left"}
            width={"13rem"}
          />
        </TruncatedDiv>

        <Label
          text={employee.phone}
          color={Color.LIGHT_BLUE}
          textTransform={"uppercase"}
          fontWeight={"normal"}
          fontSize={"12px"}
          textAlign={"center"}
          width={"6rem"}
        />
      </LabelContainer>
    </SingleParticipantsWrapper>
  );
};

export default SingleEmployee;
